import { mapKpiDataFromSheets, parseDateTime, parseHours } from "../pages/KpiDashboard/kpiSheetsMapper";
import { storeKpiData } from "../pages/KpiDashboard/KpiStorage";

// ── Google Sheets API ─────────────────────────────────────────────────────────

const SPREADSHEET_IDS = {
  stations:      "1jnxCbS3FxEBGW1jSJc9LHfDYLIWqd88z2Sr9LuNfgBc",
  anomaly:       "14P7yUXaeF0cwP4Z2FmHNFI9NZnWD1WaneYw8PhJ1naQ",
  overdueInvoice:"1yU6l2ivx7LvbsaBCsve3i9vBrlgaMj9qx2U-wWCy-nY",
  failures:      "1OgsLHK2eGFm8HX1APWZc6mcarHFWd0mj9yD-1DlqNJ4",
  marketing:     "1mi6ChmlHBN6rXOCICl6CW_T0ov58_uKrWO8ZZn_uQVk",
};

async function fetchValues(id: string, range: string, token: string): Promise<string[][]> {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${id}/values/${encodeURIComponent(range)}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Sheets API error (${range}): ${res.status} ${res.statusText}`);
  return ((await res.json()).values ?? []) as string[][];
}

async function fetchSheetTitles(id: string, token: string): Promise<string[]> {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${id}?fields=sheets.properties.title`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Sheets metadata error: ${res.status}`);
  return (await res.json()).sheets.map((s: any) => s.properties.title as string);
}

// ── Sync ──────────────────────────────────────────────────────────────────────

export async function syncKpiFromSheets(
  token: string,
  onProgress: (msg: string) => void
): Promise<void> {

  // 1. Station data
  onProgress("Fetching station data…");
  const stationRows = await fetchValues(SPREADSHEET_IDS.stations, "weekly-data!A3:V500", token);

  // 2. Anomaly stats
  onProgress("Fetching anomaly data…");
  const anomalyRows = await fetchValues(SPREADSHEET_IDS.anomaly, "stats!A2:E60", token);

  // 3. Invoice totals
  onProgress("Fetching invoice summary…");
  const invoiceSummary = await fetchValues(SPREADSHEET_IDS.overdueInvoice, "summary!A2:B60", token);

  // 4. Overdue invoice details per KW tab
  onProgress("Fetching overdue invoice details…");
  const invoiceTitles = await fetchSheetTitles(SPREADSHEET_IDS.overdueInvoice, token);
  const kwTabs = invoiceTitles.filter(t => /^KW\d+$/.test(t));
  const overdueByWeek = new Map<number, Array<{ docNumber: string; company: string; amount: number; daysOverdue: number }>>();

  for (const tab of kwTabs) {
    const kw = parseInt(tab.replace("KW", ""));
    const rows = await fetchValues(SPREADSHEET_IDS.overdueInvoice, `${tab}!A2:J200`, token);
    const invoices = rows
      .filter(r => r[0]?.startsWith("RE") && r[3] && parseFloat(r[4]) > 0)
      .map(r => ({
        docNumber:   r[0],
        company:     r[3],
        amount:      parseFloat(r[4]) || 0,
        daysOverdue: parseInt(r[9]) || 0,
      }))
      .filter(i => i.daysOverdue > 0)
      .sort((a, b) => b.daysOverdue - a.daysOverdue);
    if (invoices.length) overdueByWeek.set(kw, invoices);
  }

  // 5. Failures (aggregate raw FAULTED records by station + year + month)
  onProgress("Fetching failures data…");
  const failureTitles = await fetchSheetTitles(SPREADSHEET_IDS.failures, token);
  const dataTab = failureTitles.find(t => t.startsWith("data-"));
  const failures: Array<{ station: string; year: string; month: string; duration: string; hours: number }> = [];

  if (dataTab) {
    const failureRows = await fetchValues(SPREADSHEET_IDS.failures, `${dataTab}!A2:F5000`, token);
    const agg = new Map<string, { station: string; year: string; month: string; totalHours: number }>();
    for (const r of failureRows) {
      if (r[0] !== "FAULTED") continue;
      const dt = parseDateTime(r[1]);
      if (!dt || !r[4]) continue;
      const key = `${r[4]}|${dt.year}|${dt.month}`;
      const existing = agg.get(key);
      if (existing) {
        existing.totalHours += parseHours(r[3]);
      } else {
        agg.set(key, { station: r[4], year: String(dt.year), month: String(dt.month), totalHours: parseHours(r[3]) });
      }
    }
    for (const f of agg.values()) {
      if (f.totalHours >= 1) {
        failures.push({ station: f.station, year: f.year, month: f.month, duration: `${f.totalHours}:00:00`, hours: f.totalHours });
      }
    }
  }

  // 6. Marketing
  onProgress("Fetching marketing data…");
  const currentYear = new Date().getFullYear();
  const cplRows: string[][] = [];
  for (const year of [currentYear - 1, currentYear]) {
    const rows = await fetchValues(SPREADSHEET_IDS.marketing, `${year}!A1:M15`, token);
    rows.forEach(r => cplRows.push([String(year), ...r]));
  }

  // 7. Transform and persist
  onProgress("Building KPI dataset…");
  const entries = mapKpiDataFromSheets({ stationRows, anomalyRows, invoiceSummary, overdueByWeek, failures, cplRows });
  storeKpiData(entries);

  onProgress("Sync complete!");
}
